// src/services/buildSessionPersistenceService.ts
// Persistence layer for Build Mode session state
//
// Features:
// - Debounced autosave (500ms delay)
// - Background sync to Supabase
// - Local storage fallback for offline resilience
// - Draft recovery on app load
// - Versioned storage (user + client scoped)

import type { CampaignConstraints } from "@/context/ModeContext";
import { supabase } from "@/lib/supabaseClient";
import type { AskLadderPolicy } from "@/types/askStrategy.types";
import type {
  CampaignTypeId,
  CampaignCategory,
  CampaignObjective,
  JourneyTrigger,
} from "@/types/campaignCategory";
import {
  getScopedItem,
  setScopedItem,
  removeScopedItem,
} from "@/utils/storage";

// Uses typed Supabase client - build_session_drafts table with production hardening columns

// ============================================================================
// TYPES
// ============================================================================

/**
 * Wizard form state that needs persistence alongside BuildSession
 */
export interface WizardState {
  // Campaign Type Selection (Step 0 - new in M1)
  campaignTypeId?: CampaignTypeId | null;
  campaignCategory?: CampaignCategory | null;
  campaignObjective?: CampaignObjective | null; // Required if category = 'campaign'
  journeyTrigger?: JourneyTrigger | null; // Required if category = 'journey'

  model: "canonical" | "custom";
  constraints: CampaignConstraints;
  basics: {
    name: string;
    description: string;
    goalAmount: number;
    startDate: string;
    endDate: string;
  };
  season: string | null;
  coreStory: {
    headline?: string;
    summary?: string;
    valueProposition?: string;
    donorMotivation?: string;
  };
  narrativeSteps: Array<{
    id: string;
    campaignId: string;
    stage: string;
    title: string;
    body: string;
    channels: string[];
    primarySegment?: string;
    callToAction?: string;
    sequence: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
  // Ask Strategy
  askPolicy?: Partial<AskLadderPolicy> | null;
  askPolicyId?: string | null;
}

/**
 * Full draft including both session metadata and wizard form state
 */
export interface BuildSessionDraft {
  id?: string;
  clientId: string;
  userId: string;
  campaignId?: string;
  campaignName?: string;
  model: "canonical" | "custom";
  constraints: CampaignConstraints;
  step: number;
  totalSteps: number;
  wizardState: WizardState;
  startedAt: Date;
  updatedAt?: Date;
}

/**
 * Save state as a finite state machine
 * Formalizes the save status to prevent edge-case regressions
 */
export type SaveState =
  | "idle"
  | "saving"
  | "saved_remote"
  | "saved_local_only"
  | "failed";

export interface SaveStatus {
  state: SaveState;
  saving: boolean;
  lastSaved: Date | null;
  error: string | null;
}

/** Current schema version for wizard state - increment when breaking changes occur */
const WIZARD_STATE_SCHEMA_VERSION = 1;

/** App version for debugging */
const APP_VERSION = import.meta.env.VITE_APP_VERSION || "dev";

export type DraftScope = {
  userId?: string | null;
  clientId?: string | null;
};

// ============================================================================
// SERVICE CLASS
// ============================================================================

class BuildSessionPersistenceService {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private debounceDelay = 500; // milliseconds
  private saveInProgress = false;
  private onStatusChange?: (status: SaveStatus) => void;
  private lastSaveStatus: SaveStatus = {
    state: "idle",
    saving: false,
    lastSaved: null,
    error: null,
  };

  private readonly STORAGE_KEY = "build_session_draft";

  /** Unique ID for this editing session (for cross-tab conflict detection) */
  private readonly editSessionId = crypto.randomUUID();

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /**
   * Register a callback for save status updates
   * Useful for UI indicators (e.g., "Saving...", "All changes saved")
   */
  onSaveStatusChange(callback: (status: SaveStatus) => void) {
    this.onStatusChange = callback;
  }

  /**
   * Autosave with debouncing
   *
   * Delays the actual save operation until user stops editing.
   * Cancels pending saves if new changes come in.
   */
  autosave(draft: BuildSessionDraft, scope: DraftScope): void {
    // Clear any pending save
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Update status to indicate pending save
    this.updateStatus({ state: "saving", saving: true, error: null });

    // Save to local storage immediately as backup
    this.saveToLocalStorage(draft, scope);

    // Schedule database save after delay
    this.debounceTimer = setTimeout(() => {
      void this.saveDraft(draft);
    }, this.debounceDelay);
  }

  /**
   * Save draft to database
   *
   * Performs the actual save operation with error handling.
   * Updates save status for UI feedback.
   */
  async saveDraft(draft: BuildSessionDraft): Promise<boolean> {
    if (this.saveInProgress) {
      console.log(
        "[BuildSessionPersistence] Save already in progress, skipping",
      );
      return false;
    }

    this.saveInProgress = true;
    this.updateStatus({ state: "saving", saving: true, error: null });

    try {
      const upsertData = {
        client_id: draft.clientId,
        user_id: draft.userId,
        campaign_id: draft.campaignId || null,
        campaign_name: draft.campaignName || null,
        model: draft.model,
        constraints: draft.constraints,
        step: draft.step,
        total_steps: draft.totalSteps,
        wizard_state: draft.wizardState,
        started_at: draft.startedAt.toISOString(),
        // Production hardening fields
        schema_version: WIZARD_STATE_SCHEMA_VERSION,
        app_version: APP_VERSION,
        edit_session_id: this.editSessionId,
      };

      const { error } = await supabase
        .from("build_session_drafts")
        .upsert(upsertData as any, { onConflict: "client_id,user_id" });

      if (error) {
        throw error;
      }

      this.updateStatus({
        state: "saved_remote",
        saving: false,
        lastSaved: new Date(),
        error: null,
      });

      return true;
    } catch (error) {
      console.error("[BuildSessionPersistence] Failed to save draft:", error);

      this.updateStatus({
        state: "saved_local_only",
        saving: false,
        lastSaved: new Date(),
        error:
          error instanceof Error
            ? error.message
            : "Failed to save to server. Changes are saved locally.",
      });

      // Still return true since we saved to localStorage
      return true;
    } finally {
      this.saveInProgress = false;
    }
  }

  /**
   * Force immediate save (bypasses debounce)
   *
   * Useful when user explicitly clicks "Save" or before navigation.
   */
  async forceSave(
    draft: BuildSessionDraft,
    scope: DraftScope,
  ): Promise<boolean> {
    // Cancel any pending debounced save
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Save to localStorage first
    this.saveToLocalStorage(draft, scope);

    // Then save to database
    return this.saveDraft(draft);
  }

  /**
   * Load draft for a specific user and client
   *
   * Tries database first, falls back to localStorage.
   * Validates schema version and handles cross-tab conflicts.
   */
  async loadDraft(
    clientId: string,
    userId: string,
    scope: DraftScope,
  ): Promise<BuildSessionDraft | null> {
    try {
      const { data, error } = await supabase
        .from("build_session_drafts")
        .select("*")
        .eq("client_id", clientId)
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      // maybeSingle() returns null when no rows exist
      if (!data) {
        return this.loadFromLocalStorage(scope);
      }

      // Check schema version - discard if incompatible (newer than supported)
      if (data.schema_version > WIZARD_STATE_SCHEMA_VERSION) {
        console.warn(
          `[BuildSessionPersistence] Draft schema version ${data.schema_version} is newer than supported ${WIZARD_STATE_SCHEMA_VERSION}. Discarding draft.`,
        );
        await this.deleteDraft(clientId, userId, scope);
        return null;
      }

      return this.rowToDraft(data);
    } catch (error) {
      console.error("[BuildSessionPersistence] Failed to load draft:", error);

      // Fallback to local storage
      return this.loadFromLocalStorage(scope);
    }
  }

  /**
   * Check if this tab's edits would conflict with a remote draft
   * Returns true if the remote draft was edited by a different session
   */
  async checkForConflict(
    clientId: string,
    userId: string,
  ): Promise<{ hasConflict: boolean; remoteUpdatedAt?: Date }> {
    try {
      const { data, error } = await supabase
        .from("build_session_drafts")
        .select("edit_session_id, updated_at")
        .eq("client_id", clientId)
        .eq("user_id", userId)
        .maybeSingle();

      if (error || !data) {
        return { hasConflict: false };
      }

      const hasConflict =
        data.edit_session_id !== null &&
        data.edit_session_id !== this.editSessionId;

      return {
        hasConflict,
        remoteUpdatedAt: data.updated_at
          ? new Date(data.updated_at)
          : undefined,
      };
    } catch {
      return { hasConflict: false };
    }
  }

  /**
   * Check if a draft exists for this user/client
   */
  async hasDraft(
    clientId: string,
    userId: string,
    scope: DraftScope,
  ): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from("build_session_drafts")
        .select("id", { count: "exact", head: true })
        .eq("client_id", clientId)
        .eq("user_id", userId);

      if (error) throw error;

      if (count && count > 0) return true;

      // Check localStorage as fallback
      return this.hasLocalDraft(scope);
    } catch {
      return this.hasLocalDraft(scope);
    }
  }

  /**
   * Delete draft (on successful campaign launch or explicit cancel)
   */
  async deleteDraft(
    clientId: string,
    userId: string,
    scope: DraftScope,
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("build_session_drafts")
        .delete()
        .eq("client_id", clientId)
        .eq("user_id", userId);

      if (error) throw error;

      // Also clear localStorage
      this.clearLocalStorage(scope);

      return true;
    } catch (error) {
      console.error("[BuildSessionPersistence] Failed to delete draft:", error);

      // Still clear localStorage
      this.clearLocalStorage(scope);

      return false;
    }
  }

  /**
   * Get current save status
   */
  getStatus(): SaveStatus {
    return { ...this.lastSaveStatus };
  }

  /**
   * Reset save status (useful when navigating away)
   */
  resetStatus(): void {
    this.lastSaveStatus = {
      state: "idle",
      saving: false,
      lastSaved: null,
      error: null,
    };
    this.onStatusChange?.(this.lastSaveStatus);
  }

  /**
   * Get the current edit session ID (for conflict detection)
   */
  getEditSessionId(): string {
    return this.editSessionId;
  }

  // ==========================================================================
  // LOCAL STORAGE HELPERS
  // ==========================================================================

  private saveToLocalStorage(
    draft: BuildSessionDraft,
    scope: DraftScope,
  ): void {
    try {
      const dataWithTimestamp = {
        ...draft,
        startedAt: draft.startedAt.toISOString(),
        _localSaveTime: new Date().toISOString(),
      };
      setScopedItem(this.STORAGE_KEY, dataWithTimestamp, {
        userId: scope.userId,
        clientId: scope.clientId,
      });
    } catch (error) {
      console.error(
        "[BuildSessionPersistence] Failed to save to localStorage:",
        error,
      );
    }
  }

  private loadFromLocalStorage(scope: DraftScope): BuildSessionDraft | null {
    try {
      const stored = getScopedItem<
        BuildSessionDraft & {
          _localSaveTime?: string;
          startedAt: string;
        }
      >(this.STORAGE_KEY, {
        userId: scope.userId,
        clientId: scope.clientId,
      });

      if (!stored) return null;

      // Remove local metadata and restore Date object
      const { _localSaveTime, startedAt, ...rest } = stored;

      return {
        ...rest,
        startedAt: new Date(startedAt),
      } as BuildSessionDraft;
    } catch (error) {
      console.error(
        "[BuildSessionPersistence] Failed to load from localStorage:",
        error,
      );
      return null;
    }
  }

  private hasLocalDraft(scope: DraftScope): boolean {
    try {
      const stored = getScopedItem(this.STORAGE_KEY, {
        userId: scope.userId,
        clientId: scope.clientId,
      });
      return stored !== null;
    } catch {
      return false;
    }
  }

  private clearLocalStorage(scope: DraftScope): void {
    try {
      removeScopedItem(this.STORAGE_KEY, {
        userId: scope.userId,
        clientId: scope.clientId,
      });
    } catch (error) {
      console.error(
        "[BuildSessionPersistence] Failed to clear localStorage:",
        error,
      );
    }
  }

  // ==========================================================================
  // STATUS MANAGEMENT
  // ==========================================================================

  private updateStatus(partial: Partial<SaveStatus>): void {
    this.lastSaveStatus = {
      ...this.lastSaveStatus,
      ...partial,
      lastSaved: partial.lastSaved || this.lastSaveStatus.lastSaved,
    };

    this.onStatusChange?.(this.lastSaveStatus);
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  private rowToDraft(row: Record<string, unknown>): BuildSessionDraft {
    return {
      id: row.id as string,
      clientId: row.client_id as string,
      userId: row.user_id as string,
      campaignId: row.campaign_id as string | undefined,
      campaignName: row.campaign_name as string | undefined,
      model: row.model as "canonical" | "custom",
      constraints: row.constraints as CampaignConstraints,
      step: row.step as number,
      totalSteps: row.total_steps as number,
      wizardState: row.wizard_state as WizardState,
      startedAt: new Date(row.started_at as string),
      updatedAt: row.updated_at
        ? new Date(row.updated_at as string)
        : undefined,
    };
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

export const buildSessionPersistenceService =
  new BuildSessionPersistenceService();
